import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Terminal from "@/pages/Terminal";
import AdminLogin from "@/pages/AdminLogin";
import Dashboard from "@/pages/Dashboard";
import Accounts from "@/pages/Accounts";
import Documents from "@/pages/Documents";
import Gallery from "@/pages/Gallery";
import Pages from "@/pages/Pages";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// PWA install prompt handler
const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => setShowInstall(false);

  return { showInstall, install, dismiss };
};

// Offline detection
const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#22c55e] text-glow font-mono">
          [ AUTHENTICATING... ]
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin-5390214552274" replace />;
  }

  return children;
};

// PWA Install Banner Component
const PWAInstallBanner = ({ show, onInstall, onDismiss }) => {
  if (!show) return null;

  return (
    <div className="pwa-banner" data-testid="pwa-install-banner">
      <span className="text-sm text-[#22c55e]">
        [ INSTALL KMB.LOL AS APP ]
      </span>
      <div className="flex gap-2">
        <button
          onClick={onInstall}
          className="px-4 py-2 bg-[#22c55e] text-[#050505] font-mono text-sm uppercase tracking-wider hover:bg-[#4ade80] transition-colors"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
        <button
          onClick={onDismiss}
          className="px-4 py-2 border border-[#15803d] text-[#22c55e] font-mono text-sm uppercase tracking-wider hover:border-[#22c55e] transition-colors"
          data-testid="pwa-dismiss-btn"
        >
          Later
        </button>
      </div>
    </div>
  );
};

// Offline Banner
const OfflineBanner = ({ isOnline }) => {
  if (isOnline) return null;

  return (
    <div className="offline-banner" data-testid="offline-banner">
      [ OFFLINE MODE - Some features unavailable ]
    </div>
  );
};

function AppContent() {
  const { showInstall, install, dismiss } = usePWAInstall();
  const isOnline = useOnlineStatus();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Scanline overlay */}
      <div className="scanlines" aria-hidden="true" />
      
      {/* Offline indicator */}
      <OfflineBanner isOnline={isOnline} />

      <BrowserRouter>
        <Routes>
          {/* Public terminal */}
          <Route path="/" element={<Terminal />} />

          {/* Admin routes */}
          <Route path="/admin-5390214552274" element={<AdminLogin />} />
          <Route
            path="/admin-5390214552274/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-5390214552274/dashboard/accounts"
            element={
              <ProtectedRoute>
                <Accounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-5390214552274/dashboard/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-5390214552274/dashboard/gallery"
            element={
              <ProtectedRoute>
                <Gallery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-5390214552274/dashboard/pages"
            element={
              <ProtectedRoute>
                <Pages />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      {/* PWA Install Banner */}
      <PWAInstallBanner
        show={showInstall}
        onInstall={install}
        onDismiss={dismiss}
      />

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid #15803d",
            color: "#22c55e",
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
